import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription, of, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../../auth-pages/services/auth.service';
import { NotificationApiService } from './notification-api.service';
import { UserNotification } from '../models/notification.model';

/**
 * Estado global del centro de notificaciones in-app.
 *
 * - Mantiene `unreadCount$` (BehaviorSubject) para el badge de la campana.
 * - Hace polling cada 30s del endpoint barato `/unread-count` (timer + switchMap),
 *   tolerante a errores (catchError → no rompe el stream) y pausable sin fugas.
 * - Centraliza el mapeo `contextEntityType + contextEntityId` → ruta hash real
 *   del frontend, ya que el backend devuelve rutas tentativas en español.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  /** Intervalo de polling del contador de no leídas (ms). */
  private readonly POLL_INTERVAL_MS = 30_000;

  private readonly _unreadCount = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this._unreadCount.asObservable();

  private pollingSub?: Subscription;

  constructor(
    private api: NotificationApiService,
    private auth: AuthService
  ) {}

  // ── Polling ──────────────────────────────────────────────────────────

  /** Arranca el polling del contador (idempotente). Llamar cuando el usuario está autenticado. */
  startPolling(): void {
    if (this.pollingSub) return;

    this.pollingSub = timer(0, this.POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => {
          // Si la sesión se perdió, no golpear la API; conservar el valor actual.
          if (!this.auth.isAuthenticated() || this.auth.getBusinessId() == null) {
            return of({ count: this._unreadCount.value });
          }
          return this.api.getUnreadCount().pipe(
            // Un fallo puntual de red no debe detener el timer.
            catchError(() => of({ count: this._unreadCount.value }))
          );
        })
      )
      .subscribe(res => this._unreadCount.next(res.count));
  }

  /** Detiene el polling y libera la suscripción. */
  stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  /** Fuerza una lectura inmediata del contador (p.ej. tras marcar todas como leídas). */
  refreshUnreadCount(): void {
    if (!this.auth.isAuthenticated() || this.auth.getBusinessId() == null) return;
    this.api.getUnreadCount()
      .pipe(catchError(() => of({ count: this._unreadCount.value })))
      .subscribe(res => this._unreadCount.next(res.count));
  }

  // ── Mutaciones optimistas del contador ───────────────────────────────

  setUnreadCount(value: number): void {
    this._unreadCount.next(Math.max(0, value));
  }

  decrementUnread(): void {
    this._unreadCount.next(Math.max(0, this._unreadCount.value - 1));
  }

  // ── Mapeo de ruta (contextEntityType + contextEntityId → ruta hash real) ─

  /**
   * Traduce una notificación a la ruta hash real del frontend.
   *
   * El backend devuelve `actionRoute` tentativas (p.ej. "/reuniones/{id}",
   * "/llamadas/historial") que NO coinciden con las rutas reales del router.
   * Resolvemos a partir del tipo+id de la entidad usando las rutas REALES
   * verificadas en `app.routes.ts`; si no hay id, caemos a la lista de la entidad.
   * Para GENERIC (o un `contextEntityType` no mapeado) se usa `actionRoute` tal
   * cual si viene poblado; en caso contrario no se navega (null).
   *
   * Rutas reales verificadas (app.routes.ts):
   *  - TASK    → /tareas?openTask=:id    (modal de detalle sobre la lista /tareas)
   *  - TICKET  → /tickets?openTicket=:id (abre el modal de detalle sobre la lista)
   *  - MEETING → /meeting?openMeeting=:id (modal de detalle sobre la lista /meeting)
   *  - DEAL    → /deals/editar/:id       (lista /deals)
   *  - CONTACT → /contactos/editar/:id   (lista /contactos)
   *  - LEAD    → /leads/editar/:id       (lista /leads)
   *  - COMPANY → /empresas/editar/:id    (lista /empresas)
   *  - QUOTE   → /cotizaciones/detalle/:id (lista /cotizaciones)
   *  - CALL    → /call                   (no hay detalle por id → lista de llamadas)
   *  - CONVERSATION → /agent/conversations (no hay detalle por id → bandeja de agente)
   */
  resolveRoute(n: UserNotification): string | null {
    const id = n.contextEntityId;

    // El CSAT negativo apunta a una conversación, pero lo que el supervisor tiene que
    // revisar es la VALORACIÓN, no el chat: el backend ya manda `/chatflows/{id}/csat` en
    // actionRoute y aquí se descartaba por caer en el caso CONVERSATION, que lleva siempre
    // a la bandeja de agente (INC_CRM_019 (8) inc60). Se añade la conversación como query
    // param para poder resaltar la respuesta exacta que disparó el aviso.
    if (n.type === 'CSAT_NEGATIVE' && n.actionRoute) {
      return id != null ? `${n.actionRoute}?csatConversation=${id}` : n.actionRoute;
    }

    switch (n.contextEntityType) {
      case 'TASK':
        // La notificación (p.ej. "te agregaron como seguidor") informa de algo que
        // pasó en la tarea, así que debe llevar al DETALLE, no al formulario de
        // edición. La tarea no tiene página de detalle propia sino un modal sobre
        // la lista, igual que ticket y reunión → query param `openTask`.
        return id != null ? `/tareas?openTask=${id}` : '/tareas';
      case 'TICKET':
        // INC 50: en vez de una página de detalle completa, abrimos el modal de
        // detalle sobre la lista mediante el query param `openTicket`.
        return id != null ? `/tickets?openTicket=${id}` : '/tickets';
      case 'MEETING':
        // INC 83: la notificación informa de una reunión agendada, así que debe
        // llevar al DETALLE, no al formulario de edición. El detalle de reunión no
        // es una página propia sino un modal sobre la lista, igual que el ticket,
        // así que se abre con el query param `openMeeting`.
        return id != null ? `/meeting?openMeeting=${id}` : '/meeting';
      case 'DEAL':
        return id != null ? `/deals/editar/${id}` : '/deals';
      case 'CONTACT':
        return id != null ? `/contactos/editar/${id}` : '/contactos';
      case 'LEAD':
        return id != null ? `/leads/editar/${id}` : '/leads';
      case 'COMPANY':
        return id != null ? `/empresas/editar/${id}` : '/empresas';
      case 'QUOTE':
        return id != null ? `/cotizaciones/detalle/${id}` : '/cotizaciones';
      case 'CALL':
        // No existe ruta de detalle de llamada por id → siempre a la lista.
        return '/call';
      case 'CONVERSATION':
        // No existe ruta de detalle de conversación por id → bandeja de agente.
        return '/agent/conversations';
      case 'MARKETING_CAMPAIGN':
        // Correo programado que volvió a borrador: al formulario, donde se ve el motivo.
        return id != null ? `/marketing-email/editar/${id}` : '/marketing-email';
      default:
        return n.actionRoute ? n.actionRoute : null;
    }
  }
}
